import { type PaginatedMaintenance } from "@/api/resources/maintenances/types";
import { type PaginatedMaintenanceStrategy } from "@/api/resources/maintenanceStrategies/types";
import { MaintenanceStatus } from "@/supabase.types";
import { getDateISO, getDateNow } from "@/utils/dates";
import { getRRuleDatesBetween } from "@/utils/rrule";

export const VIRTUAL_MAINTENANCE_PREFIX = `VIRTUAL`;

export const getExcludedMaintenancesByStrategy = (maintenances: PaginatedMaintenance[]) => {
  return maintenances.reduce<Record<string, string[]>>((reduceObj, item) => {
    if (item.maintenance_strategy_id && item.scheduled_at) {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      (reduceObj[item.maintenance_strategy_id] = reduceObj[item.maintenance_strategy_id] || []).push(item.scheduled_at);
    }
    return reduceObj;
  }, {});
};

interface GetMaintenancesUpcomingVirtualProps {
  strategies: PaginatedMaintenanceStrategy[];
  excluded?: Record<string, string[]>;
  from: string;
  until: string;
}
export const getMaintenancesUpcomingVirtual = ({
  strategies,
  from,
  until,
  excluded,
}: GetMaintenancesUpcomingVirtualProps): PaginatedMaintenance[] => {
  const maintenances: PaginatedMaintenance[] = [];
  for (const strategy of strategies) {
    const {
      id: maintenance_strategy_id,
      user_id,
      assignee_id,
      title,
      workspace_id,
      deployment_id,
      maintenance_strategy_intervals,
    } = strategy;

    for (const interval of maintenance_strategy_intervals) {
      const { schedule_interval } = interval;
      let intervalDates = getRRuleDatesBetween(schedule_interval, from, until);

      // filter out all excluded dates of a strategy
      if (excluded?.[maintenance_strategy_id] && excluded[maintenance_strategy_id].length > 0) {
        intervalDates = intervalDates.filter(
          (intervalDate) =>
            !excluded[maintenance_strategy_id].map((date) => getDateISO(date)).includes(getDateISO(intervalDate)),
        );
      }

      for (const date of intervalDates) {
        maintenances.push({
          created_at: getDateNow(),
          updated_at: getDateNow(),
          scheduled_at: date,
          id: `${VIRTUAL_MAINTENANCE_PREFIX}:${maintenance_strategy_id}@${date}`,
          serial_id: 1,
          serial_id_prefix: "XXX",
          public_id: "XXX-1",
          maintenance_strategy_id,
          notes: "",
          planned_date: date,
          planned_until: null,
          parent_maintenance_id: null,
          parent_maintenance: [],
          maintenance_items: [],
          child_cases: [],
          child_maintenances: [],
          planned_at: null,
          resolved_at: null,
          skip_reason: null,
          started_at: null,
          status: MaintenanceStatus.scheduled,
          title,
          user_id,
          assignee_id,
          assignee: null,
          workspace_id,
          deployment_id,
          resolution_time: null, // FIXME: fixed by https://github.com/supabase/cli/issues/2394
          maintenance_strategy: strategy,
          maintenances_to_devices: [],
          maintenance_tags: [],
        });
      }
    }
  }
  return maintenances;
};

export const isVirtualMaintenance = (maintenance?: PaginatedMaintenance) =>
  Boolean(maintenance && maintenance.id.startsWith(VIRTUAL_MAINTENANCE_PREFIX));
